"use client";

import { useState, type ReactNode } from "react";
import { Play } from "lucide-react";
import { videoEmbedSrc, type VideoEmbed } from "@/lib/embeds/video";

/**
 * The Hero's visual frame playing an intro video IN PLACE — a click-to-play
 * facade. Until the visitor presses play, the frame shows its poster (the
 * Hero's own `visual`, passed in as `children` and server-rendered, so it stays
 * the LCP image) with a play button over it; no third-party script, cookie or
 * request happens before that click. Pressing play swaps in the player iframe,
 * autoplaying, since the visitor has already asked for it.
 *
 * The iframe follows docs/synced/embeds.md's snippet: it fills the frame (the
 * frame owns the aspect ratio), and carries `allow` + `allowfullscreen` and a
 * title. The frame itself is the parent's: this renders only what goes inside.
 */
export function HeroVideoFrame({
  video,
  title,
  playLabel,
  children,
}: {
  video: VideoEmbed;
  /** The iframe's accessible name — the hero's headline. */
  title: string;
  /** Chrome copy for the button ("Play the video"). */
  playLabel: string;
  children?: ReactNode;
}) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <iframe
        src={videoEmbedSrc(video, { autoplay: true })}
        title={title}
        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 size-full border-0 bg-black"
      />
    );
  }

  return (
    <>
      {/* The ground under a poster-less frame, so the button never floats on
          nothing. */}
      <span aria-hidden className="absolute inset-0 bg-teal-deep" />
      {children}
      <button
        type="button"
        onClick={() => setPlaying(true)}
        aria-label={`${playLabel}: ${title}`}
        className="group absolute inset-0 grid size-full place-items-center bg-black/15 transition-colors hover:bg-black/5 focus-visible:outline-none"
      >
        <span className="grid size-16 place-items-center rounded-full bg-white/20 ring-1 ring-white/40 backdrop-blur-sm transition-transform duration-300 group-hover:scale-105 group-focus-visible:ring-2 group-focus-visible:ring-ring md:size-20">
          <Play className="ml-1 size-7 fill-white text-white md:size-8" aria-hidden />
        </span>
      </button>
    </>
  );
}
